"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn, titleCase } from "@/lib/utils";
import { submitProspectForm } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { PaymentPlanCard } from "./PaymentPlanCard";
import { lgasByState, getLgasForState } from "@/data/lgas";
import type {
  ProspectFormData,
  ProspectFormErrors,
  ProspectType,
} from "@/types";
import {
  PROSPECT_NAME_MAX_LENGTH,
  createProspectPayload,
  isNigerianPhoneInputAllowed,
  isProspectNameInputAllowed,
  normalizeNigerianPhone,
  switchProspectType,
  validateProspectForm,
} from "../form";
import type { FieldErrorProps, ProspectFormProps } from "../types";

const initialFormData: ProspectFormData = {
  prospectType: "customer",
  firstName: "",
  lastName: "",
  nin: "",
  phoneNumber: "",
  address: "",
  state: "",
  lga: "",
  categoryId: "",
  deviceId: "",
};

const states = Object.keys(lgasByState);

// Requested submission limit for the address field; raise if the API ever
// enforces a different maximum.
const ADDRESS_MAX_LENGTH = 100;

const labelClassName =
  "mb-1.5 block text-sm font-medium tracking-label text-black-100";

const inputClassName =
  "w-full rounded-lg bg-white px-4 py-2.5 text-black-100 shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a984d] outline-none transition-shadow duration-200 placeholder:text-grey-400 hover:shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a9880] focus:shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_1px_#006e19,0_0_0_4px_rgba(0,110,25,0.1)]";

const selectClassName = cn(
  inputClassName,
  "cursor-pointer appearance-none pr-11 disabled:cursor-not-allowed disabled:bg-grey-300 disabled:text-grey-400 disabled:shadow-[0_0_0_0.5px_#949a984d] disabled:hover:shadow-[0_0_0_0.5px_#949a984d]"
);

function SelectChevron() {
  return (
    <svg
      className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-grey-200"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RequiredMark() {
  return (
    <span aria-hidden="true" className="text-red-600">
      *
    </span>
  );
}

function FieldError({ id, message }: FieldErrorProps) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      className="mt-2 min-w-0 whitespace-pre-line text-left text-sm leading-5 text-red-500"
    >
      {message}
    </p>
  );
}

function ProspectForm({ slug, form, introduction }: ProspectFormProps) {
  const [formData, setFormData] = useState<ProspectFormData>(initialFormData);
  const [errors, setErrors] = useState<ProspectFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showNin, setShowNin] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState("");

  const categoriesWithAssets = form.categories.filter(
    (category) => category.assets.length > 0
  );
  const activeCategory =
    categoriesWithAssets.find((c) => c.categoryId === activeCategoryId) ??
    categoriesWithAssets[0] ??
    null;

  const handleTablistKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const index = categoriesWithAssets.findIndex(
      (c) => c.categoryId === activeCategory?.categoryId
    );
    const delta = e.key === "ArrowRight" ? 1 : -1;
    const next =
      categoriesWithAssets[
        (index + delta + categoriesWithAssets.length) %
          categoriesWithAssets.length
      ];
    setActiveCategoryId(next.categoryId);
    document.getElementById(`category-tab-${next.categoryId}`)?.focus();
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    if (
      (name === "firstName" || name === "lastName") &&
      !isProspectNameInputAllowed(value)
    ) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Use letters, spaces, apostrophes, hyphens or periods only",
      }));
      return;
    }
    if (name === "phoneNumber" && !isNigerianPhoneInputAllowed(value)) {
      setErrors((prev) => ({
        ...prev,
        phoneNumber:
          "Use up to 11 digits starting with 0, or +234 followed by 10 digits",
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Changing state invalidates the chosen LGA
      ...(name === "state" ? { lga: "" } : {}),
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
      ...(name === "state" ? { lga: undefined } : {}),
    }));
  };

  const handleTypeChange = (prospectType: ProspectType) => {
    setFormData((prev) => switchProspectType(prev, prospectType));
    setErrors((prev) => ({ ...prev, deviceId: undefined, submit: undefined }));
  };

  const selectItem = (categoryId: string, deviceId: string) => {
    setFormData((prev) => ({ ...prev, categoryId, deviceId }));
    setErrors((prev) => ({ ...prev, deviceId: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const newErrors = validateProspectForm(formData, form.categories);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      document.getElementById(Object.keys(newErrors)[0])?.focus();
      return;
    }

    setErrors({});
    setIsLoading(true);
    try {
      await submitProspectForm(slug, createProspectPayload(formData));
      setSubmitted(true);
    } catch (error) {
      setErrors({
        submit:
          error instanceof TypeError
            ? "Unable to submit. Check your connection and try again."
            : error instanceof Error && error.message.trim()
              ? error.message
              : "Failed to submit. Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!submitted && introduction}
      <div className="rounded-xl bg-white p-5 shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a984d] sm:p-8 lg:p-10">
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              className="flex flex-col items-center py-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <motion.div
                className="mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <svg
                  width="89"
                  height="86"
                  viewBox="0 0 89 86"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <rect
                    width="88.8898"
                    height="85.7498"
                    rx="42.8749"
                    fill="#006E19"
                  />
                  <path
                    d="M40.5421 52.4796C40.2108 52.4796 39.8797 52.3446 39.6273 52.0762L31.7026 43.6422C31.1966 43.1039 31.1966 42.2334 31.7026 41.6951C32.2084 41.1568 33.026 41.1568 33.532 41.6951L40.5421 49.1555L55.4774 33.2611C55.9832 32.7228 56.8008 32.7228 57.3068 33.2611C57.8126 33.7997 57.8126 34.6697 57.3068 35.2085L41.4571 52.0762C41.2047 52.3446 40.8732 52.4796 40.5421 52.4796Z"
                    fill="white"
                  />
                </svg>
              </motion.div>
              <h1
                ref={(heading) => heading?.focus()}
                tabIndex={-1}
                className="mb-2 text-xl font-semibold tracking-copy text-black-100 outline-none lg:text-2xl"
              >
                Thank you, {formData.firstName.trim()}!
              </h1>
              <p className="max-w-md text-sm text-grey-200 sm:text-base">
                We&apos;ve received your details. A Pluck representative will
                reach out to you shortly on{" "}
                {normalizeNigerianPhone(formData.phoneNumber)}.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={handleSubmit}
              noValidate
              aria-busy={isLoading}
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <fieldset>
                <legend className={labelClassName}>
                  I’m signing up as a <RequiredMark />
                </legend>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { value: "customer", label: "Customer" },
                      { value: "sales_agent", label: "Sales Agent" },
                    ] as const
                  ).map(({ value, label }) => (
                    <label
                      key={value}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium focus-within:ring-2 focus-within:ring-primary/40",
                        formData.prospectType === value
                          ? "border-primary bg-green-2300 text-primary"
                          : "border-border-2 text-black-100"
                      )}
                    >
                      <input
                        disabled={isLoading}
                        type="radio"
                        name="prospectType"
                        value={value}
                        checked={formData.prospectType === value}
                        onChange={() => handleTypeChange(value)}
                        className="accent-primary"
                        required
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <p className="text-sm text-grey-200">All fields are required.</p>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="firstName" className={labelClassName}>
                    First name <RequiredMark />
                  </label>
                  <input
                    disabled={isLoading}
                    id="firstName"
                    type="text"
                    name="firstName"
                    required
                    autoComplete="given-name"
                    maxLength={PROSPECT_NAME_MAX_LENGTH}
                    value={formData.firstName}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Ada"
                    aria-invalid={Boolean(errors.firstName)}
                    aria-describedby={
                      errors.firstName ? "firstName-error" : undefined
                    }
                  />
                  <FieldError id="firstName-error" message={errors.firstName} />
                </div>

                <div>
                  <label htmlFor="lastName" className={labelClassName}>
                    Last name <RequiredMark />
                  </label>
                  <input
                    disabled={isLoading}
                    id="lastName"
                    type="text"
                    name="lastName"
                    autoComplete="family-name"
                    maxLength={PROSPECT_NAME_MAX_LENGTH}
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Obi"
                    aria-invalid={Boolean(errors.lastName)}
                    aria-describedby={
                      errors.lastName ? "lastName-error" : undefined
                    }
                  />
                  <FieldError id="lastName-error" message={errors.lastName} />
                </div>
              </div>

              <div>
                <label htmlFor="phoneNumber" className={labelClassName}>
                  Phone number <RequiredMark />
                </label>
                <input
                  disabled={isLoading}
                  id="phoneNumber"
                  type="tel"
                  name="phoneNumber"
                  autoComplete="tel"
                  maxLength={25}
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="0803 123 4567"
                  aria-invalid={Boolean(errors.phoneNumber)}
                  aria-describedby={
                    errors.phoneNumber ? "phoneNumber-error" : undefined
                  }
                />
                <FieldError
                  id="phoneNumber-error"
                  message={errors.phoneNumber}
                />
              </div>

              <div>
                <label htmlFor="nin" className={labelClassName}>
                  National Identification Number (NIN) <RequiredMark />
                </label>
                <div className="relative">
                  <input
                    disabled={isLoading}
                    id="nin"
                    type={showNin ? "text" : "password"}
                    inputMode="numeric"
                    autoComplete="new-password"
                    name="nin"
                    required
                    maxLength={11}
                    value={formData.nin}
                    onChange={handleChange}
                    className={cn(inputClassName, "pr-11")}
                    placeholder="12345678901"
                    aria-invalid={Boolean(errors.nin)}
                    aria-describedby={errors.nin ? "nin-error" : undefined}
                  />
                  <button
                    disabled={isLoading}
                    type="button"
                    onClick={() => setShowNin((prev) => !prev)}
                    aria-label={showNin ? "Hide NIN" : "Show NIN"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-grey-200 transition-colors duration-200 hover:text-black-100 focus:outline-none focus-visible:shadow-[0_0_0_1px_#006e19,0_0_0_4px_rgba(0,110,25,0.1)]"
                  >
                    {showNin ? (
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 3l18 18M10.6 10.7a2 2 0 002.8 2.8M7.4 7.5C5.5 8.6 4 10.2 3 12c1.8 3.3 5.1 6 9 6 1.5 0 2.9-.4 4.2-1M9.9 5.2A9.8 9.8 0 0112 5c3.9 0 7.2 2.7 9 7a13.3 13.3 0 01-2 2.9"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 12c1.8-4.3 5.1-7 9-7s7.2 2.7 9 7c-1.8 4.3-5.1 7-9 7s-7.2-2.7-9-7z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="2.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <FieldError id="nin-error" message={errors.nin} />
              </div>

              <div>
                <label htmlFor="address" className={labelClassName}>
                  Address <RequiredMark />
                </label>
                <textarea
                  disabled={isLoading}
                  id="address"
                  name="address"
                  required
                  autoComplete="street-address"
                  rows={3}
                  maxLength={ADDRESS_MAX_LENGTH}
                  value={formData.address}
                  onChange={handleChange}
                  className={cn(inputClassName, "block resize-none")}
                  placeholder="12 Admiralty Way, Lekki Phase 1"
                  aria-invalid={Boolean(errors.address)}
                  aria-describedby={
                    errors.address ? "address-error" : undefined
                  }
                />
                <div className="flex items-start justify-between gap-3">
                  <FieldError id="address-error" message={errors.address} />
                  <p
                    className={cn(
                      "ml-auto mt-2 shrink-0 pr-4 text-xs leading-5",
                      formData.address.length >= ADDRESS_MAX_LENGTH
                        ? "text-red-500"
                        : "text-grey-200"
                    )}
                  >
                    {formData.address.length}/{ADDRESS_MAX_LENGTH}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="state" className={labelClassName}>
                    State <RequiredMark />
                  </label>
                  <div className="relative">
                    <select
                      id="state"
                      disabled={isLoading}
                      name="state"
                      required
                      value={formData.state}
                      onChange={handleChange}
                      className={selectClassName}
                      aria-invalid={Boolean(errors.state)}
                      aria-describedby={
                        errors.state ? "state-error" : undefined
                      }
                    >
                      <option value="">Select a state</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <SelectChevron />
                  </div>
                  <FieldError id="state-error" message={errors.state} />
                </div>

                <div>
                  <label htmlFor="lga" className={labelClassName}>
                    LGA <RequiredMark />
                  </label>
                  <div className="relative">
                    <select
                      id="lga"
                      name="lga"
                      required
                      value={formData.lga}
                      onChange={handleChange}
                      disabled={isLoading || !formData.state}
                      className={selectClassName}
                      aria-invalid={Boolean(errors.lga)}
                      aria-describedby={errors.lga ? "lga-error" : undefined}
                    >
                      <option value="">
                        {formData.state
                          ? "Select an LGA"
                          : "Select a state first"}
                      </option>
                      {getLgasForState(formData.state).map((lga) => (
                        <option key={lga} value={lga}>
                          {lga}
                        </option>
                      ))}
                    </select>
                    <SelectChevron />
                  </div>
                  <FieldError id="lga-error" message={errors.lga} />
                </div>
              </div>

              {formData.prospectType === "customer" && (
                <fieldset
                  id="deviceId"
                  tabIndex={-1}
                  aria-invalid={Boolean(errors.deviceId)}
                  aria-describedby={
                    errors.deviceId ? "deviceId-error" : undefined
                  }
                  className="space-y-6 border-t border-border-2 pt-6"
                >
                  <legend className="sr-only">
                    Device of interest (required)
                  </legend>
                  <div>
                    <p className="text-base font-semibold tracking-copy text-black-100 sm:text-lg">
                      Device of interest <RequiredMark />
                    </p>
                    <p className="mt-1 text-sm text-grey-200">
                      Choose a category, then select the device you’re
                      interested in.
                    </p>
                    <FieldError id="deviceId-error" message={errors.deviceId} />
                    {categoriesWithAssets.length === 0 && (
                      <p className="mt-2 text-sm text-grey-200">
                        No devices are available right now. Please try again
                        later or contact us on WhatsApp below.
                      </p>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div
                      role="tablist"
                      aria-label="Categories"
                      onKeyDown={handleTablistKeyDown}
                      className="flex flex-wrap gap-2"
                    >
                      {categoriesWithAssets.map((category) => {
                        const isActive =
                          category.categoryId === activeCategory?.categoryId;
                        const holdsSelection = Boolean(
                          formData.deviceId &&
                            formData.categoryId === category.categoryId
                        );
                        return (
                          <button
                            disabled={isLoading}
                            key={category.categoryId}
                            type="button"
                            role="tab"
                            id={`category-tab-${category.categoryId}`}
                            aria-selected={isActive}
                            aria-controls={`category-panel-${category.categoryId}`}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() =>
                              setActiveCategoryId(category.categoryId)
                            }
                            className={cn(
                              "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200",
                              isActive
                                ? "bg-primary text-white shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_1px_#006e19]"
                                : "bg-white text-black-100 shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a984d] hover:shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a9880] focus:outline-none focus-visible:shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_1px_#006e19,0_0_0_4px_rgba(0,110,25,0.1)]"
                            )}
                          >
                            {titleCase(category.name)}
                            <span
                              className={cn(
                                "text-xs",
                                isActive ? "text-white/70" : "text-grey-200"
                              )}
                            >
                              {category.assets.length}
                            </span>
                            {holdsSelection && (
                              <span
                                aria-hidden="true"
                                className={cn(
                                  "h-1.5 w-1.5 rounded-full",
                                  isActive ? "bg-white" : "bg-primary"
                                )}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {activeCategory && (
                      <motion.div
                        key={activeCategory.categoryId}
                        role="tabpanel"
                        id={`category-panel-${activeCategory.categoryId}`}
                        aria-labelledby={`category-tab-${activeCategory.categoryId}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-2.5"
                      >
                        {activeCategory.assets.map((asset) => {
                          const isSelected =
                            formData.deviceId === asset.deviceId;
                          return (
                            <div key={asset.deviceId}>
                              <label
                                className={cn(
                                  "flex cursor-pointer items-center gap-3 rounded-lg bg-white px-4 py-3 transition-shadow duration-200",
                                  isSelected
                                    ? "shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_1px_#006e19,0_0_0_4px_rgba(0,110,25,0.1)]"
                                    : "shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a984d] focus-within:shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_1px_#006e19,0_0_0_4px_rgba(0,110,25,0.1)] hover:shadow-[0_1px_2px_-1px_#1c27240f,0_2px_4px_-1px_#1c272409,0_0_0_0.5px_#949a9880]"
                                )}
                              >
                                <input
                                  disabled={isLoading}
                                  type="radio"
                                  name="deviceId"
                                  value={asset.deviceId}
                                  checked={isSelected}
                                  onChange={() =>
                                    selectItem(
                                      activeCategory.categoryId,
                                      asset.deviceId
                                    )
                                  }
                                  required
                                  aria-describedby={
                                    errors.deviceId
                                      ? "deviceId-error"
                                      : undefined
                                  }
                                  className="sr-only"
                                />
                                <span
                                  aria-hidden="true"
                                  className={cn(
                                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-200",
                                    isSelected
                                      ? "border-primary bg-primary"
                                      : "border-grey-400 bg-white"
                                  )}
                                >
                                  {isSelected && (
                                    <span className="h-2 w-2 rounded-full bg-white" />
                                  )}
                                </span>
                                {asset.imageUrl && (
                                  // eslint-disable-next-line @next/next/no-img-element -- image host isn't known at build time, so next/image can't be configured for it
                                  <img
                                    src={asset.imageUrl}
                                    alt=""
                                    loading="lazy"
                                    className="h-10 w-10 shrink-0 rounded-md bg-grey-300 object-cover"
                                  />
                                )}
                                <span className="text-sm font-medium text-black-100">
                                  {titleCase(asset.name)}
                                </span>
                              </label>

                              <AnimatePresence initial={false}>
                                {isSelected && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.25 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="pb-1 pl-2 pt-3">
                                      {asset.paymentPlans &&
                                      asset.paymentPlans.length > 0 ? (
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                          {asset.paymentPlans.map((plan) => (
                                            <PaymentPlanCard
                                              key={plan.id}
                                              plan={plan}
                                            />
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="rounded-md bg-grey-300 px-4 py-3 text-sm text-grey-200">
                                          No payment plans are listed for this
                                          item yet. You can still submit and
                                          we&apos;ll get back to you with
                                          options.
                                        </p>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </div>
                </fieldset>
              )}

              <div className="space-y-3">
                {errors.submit && (
                  <div
                    id="submit-error"
                    role="alert"
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left text-sm leading-5 text-red-700"
                  >
                    <p className="font-medium">Unable to submit</p>
                    <p className="mt-1 whitespace-pre-line">{errors.submit}</p>
                  </div>
                )}

                <Button
                  key={activeCategory?.categoryId}
                  type="submit"
                  entrance="fade"
                  loading={isLoading}
                  fullWidth={true}
                  isRoundedFull={true}
                >
                  Submit
                </Button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export { ProspectForm };
